'use client';

import { useEffect, useState } from 'react';
import styles from './careers.module.scss';
import LocationOnIcon from '@mui/icons-material/LocationOn';

type ApiJob = {
  id: number;
  slug: string;
  title: string;
  location?: string | null;
  work_mode: 'onsite' | 'hybrid' | 'remote';
  employment_type: 'full_time' | 'part_time' | 'contract' | 'internship';
  about: string;
  responsibilities: string;    
  requirements: string;         
  nice_to_have?: string | null; 
  what_we_offer?: string | null;
  salary_range?: string | null;
  tags?: string | null;       
  application_url?: string | null;
  status: 'draft'|'published'|'closed';
  published_at?: string | null;
};

type UiJob = {
  id: number;
  slug: string;
  title: string;
  description: string;
  responsibilities: string[];
  mandatoryCriteria: string[];
  desirableCriteria: string[];
  salary?: string;
  location?: string;
  workMode?: string;
  tags?: string[];
  applicationUrl?: string | null;
};

const prettyWorkMode = (m: ApiJob['work_mode']) =>
  m === 'onsite' ? 'On-site' : m === 'hybrid' ? 'Hybrid' : 'Remote';

const parseBulletPoints = (txt?: string | null): string[] => {
  if (!txt) return [];
  if (Array.isArray(txt)) return txt;
  let processed = txt;
   processed = processed.replace(/<br\s*\/?>/gi, '\n');
  processed = processed.replace(/<[^>]*>/g, '');
  processed = processed.replace(/&nbsp;/g, ' ')
                       .replace(/&amp;/g, '&')
                       .replace(/&lt;/g, '<')
                       .replace(/&gt;/g, '>')
                       .replace(/&quot;/g, '"')
                       .replace(/&#39;/g, "'");
  let lines = processed.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
  if (lines.length > 1) return lines;
  
  lines = processed.split(/[•\-*]\s+/).map(s => s.trim()).filter(Boolean);
  if (lines.length > 1) return lines;
  
  lines = processed.split(/\d+\.\s+/).map(s => s.trim()).filter(Boolean);
  if (lines.length > 1) return lines;
  
  if (processed.includes('. ') && processed.split('. ').length > 1) {
    lines = processed.split(/\.\s+(?=[A-Z])/).map(s => s.trim() + (s.endsWith('.') ? '' : '.'));
    if (lines.length > 1) return lines;
  }
  
  if (processed.includes('\n')) {
    lines = processed.split('\n').map(s => s.trim()).filter(Boolean);
    if (lines.length > 1) return lines;
  }
  
  return [processed];
};

const renderFormattedText = (text: string) => {
  if (!text) return null;
  
  if (text.includes('<') && text.includes('>')) {
    const plainText = text.replace(/<br\s*\/?>/gi, '\n')
                         .replace(/<[^>]*>/g, '');
    
    return plainText.split('\n').map((line, i) => (
      <p key={i} className={styles.formattedText}>{line}</p>
    ));
  }
  
  return text.split('\n').map((line, i) => (
    <p key={i} className={styles.formattedText}>{line}</p>
  ));
};

const splitTags = (txt?: string | null) =>
  (txt ?? '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

const trackEvent = (eventName: string, payload: Record<string, any> = {}) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', eventName, payload);
  }
};

export default function JobListing() {
  const [expandedJob, setExpandedJob] = useState<number | null>(null);
  const [jobs, setJobs] = useState<UiJob[]>([]);
  const [loading, setLoading] = useState(true);

  const toggleJob = (id: number, title: string) => {
    setExpandedJob(prev => (prev === id ? null : id));
    trackEvent('job_toggle', { job_id: id, job_title: title });
  };

  const handleApplyClick = (job: UiJob) => {
    trackEvent('apply_clicked', { job_id: job.id, job_title: job.title });

    const url =
      job.applicationUrl && job.applicationUrl.trim().length
        ? job.applicationUrl
        : `/jobvacancies/apply/${job.slug}`;

    setTimeout(() => {
      window.open(url, '_blank', 'noopener,noreferrer');
    }, 120);
  };

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch('/api/careers/jobs?status=published&ordering=-published_at', {
          cache: 'no-store',
          credentials: 'include',
        });
        const raw = await res.json().catch(() => null);

        const list: any[] = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.results)
          ? raw.results
          : [];
          
        const normalized: UiJob[] = list.map((j: any) => {
          const description = j.about ?? j.description ?? '';
          const responsibilities = Array.isArray(j.responsibilities)
            ? j.responsibilities
            : parseBulletPoints(j.responsibilities);
            
          const mandatory = Array.isArray(j.mandatoryCriteria)
            ? j.mandatoryCriteria
            : parseBulletPoints(j.requirements);
            
          const desirable = Array.isArray(j.desirableCriteria)
            ? j.desirableCriteria
            : parseBulletPoints(j.nice_to_have);
            
          const tags = Array.isArray(j.tags) ? j.tags : splitTags(j.tags);

          return {
            id: Number(j.id),
            slug: j.slug ?? '',
            title: j.title ?? '',
            description,
            responsibilities,
            mandatoryCriteria: mandatory,
            desirableCriteria: desirable,
            salary: j.salary_range ?? j.salary ?? undefined,
            location: j.location ?? undefined,
            workMode: j.workMode ?? prettyWorkMode(j.work_mode),
            tags,
            applicationUrl: j.application_url ?? j.applyUrl ?? null,
          };
        });

        if (!abort) setJobs(normalized);
      } catch (error) {
        console.error('Error fetching jobs:', error);
        if (!abort) setJobs([]);
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, []);

  if (loading) {
    return (
      <div className={styles.jobCard}>
        <div className={styles.jobHeader}>
          <div className={styles.jobHeaderContent}>
            <div className={styles.skeletonTitle} />
            <div className={styles.skeletonLine} />
            <div className={styles.skeletonLine} />
          </div>
          <div className={styles.skeletonBtn} />
        </div>
      </div>
    );
  }

  if (!jobs.length) {
    return (
      <div className={styles.jobCard} style={{ padding: 18, textAlign: 'center' }}>
        <p style={{ margin: 0, opacity: 0.8 }}>No open positions right now.</p>
      </div>
    );
  }

  return (
    <div>
      {jobs.map((job) => (
        <div key={job.id} className={styles.jobCard}>
          <div
            className={`${styles.jobHeader} ${expandedJob === job.id ? styles.expanded : ''}`}
            onClick={() => toggleJob(job.id, job.title)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleJob(job.id, job.title);
              }
            }}
          >
            <div className={styles.jobHeaderContent}>
              <h3>{job.title}</h3>
              <div className={styles.jobMeta}>
                {job.location && (
                  <span className={styles.location}>
                    <LocationOnIcon fontSize="small" sx={{ marginRight: '4px' }} />
                    {job.location}
                  </span>
                )}
                {job.workMode && <span className={styles.type}>{job.workMode}</span>}
              </div>
              {!!job.tags?.length && (
                <div className={styles.tags}>
                  {job.tags.map((tag, i) => (
                    <span key={i} className={styles.tag}>{tag}</span>
                  ))}
                </div>
              )}
            </div>

            <button
              className={styles.expandButton}
              type="button"
              aria-label={expandedJob === job.id ? 'Collapse job details' : 'Expand job details'}
              onClick={(e) => {
                e.stopPropagation();
                toggleJob(job.id, job.title);
              }}
            >
              {expandedJob === job.id ? '−' : '+'}
            </button>
          </div>

          {expandedJob === job.id && (
            <div className={styles.jobDetails}>
              <div className={styles.section}>
                <h4>Job Description</h4>
                {renderFormattedText(job.description)}
              </div>

              {!!job.responsibilities.length && (
                <div className={styles.section}>
                  <h4>Principal Duties and Responsibilities</h4>
                  <ul className={styles.bulletList}>
                    {job.responsibilities.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}

              {!!job.mandatoryCriteria.length && (
                <div className={styles.section}>
                  <h4>Selection Criteria : Mandatory</h4>
                  <ul className={styles.bulletList}>
                    {job.mandatoryCriteria.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}

              {!!job.desirableCriteria.length && (
                <div className={styles.section}>
                  <h4>Selection Criteria : Desirable</h4>
                  <ul className={styles.bulletList}>
                    {job.desirableCriteria.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}

              {job.salary && (
                <div className={styles.section}>
                  <h4>Salary</h4>
                  <p>{job.salary}</p>
                </div>
              )}

              <div className={styles.applySection}>
                <button
                  className={styles.ctaButton}
                  type="button"
                  onClick={() => handleApplyClick(job)}
                  aria-label={`Apply for ${job.title} position`}
                >
                  Apply now
                </button>
                <p className={styles.applyNote}>
                  You'll be redirected to the official application page.
                </p>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}